import { Link, useLocation } from "react-router"
import cx from "classnames"

import TakeActionCard from "../../components/TakeActionCard"
import WizardHeader from "../../components/WizardHeader"
import WizardFooter from "../../components/WizardFooter"
import CornerRibbon from "../../components/CornerRibbon"
import { SubmitButton } from "../../components/SubmitButton"
import { useAccountUrlPrefix } from "../../hooks"
import useMediaQuery from "../../hooks/useMediaQuery"
import { useGovernmentStore } from "../../stores/useGovernmentStore"

import { AccountTypes } from "@/types/account"

export interface GovtWizardTakeActionImplementationsProps {
  account: AccountTypes | undefined
  handlePrevious: () => void
  handleSubmit: (ncxServices?: boolean) => void
  isYesBtn: boolean
  isPending: boolean
}

const GovtWizardTakeActionImplementations = ({
  account,
  handlePrevious,
  handleSubmit,
  isYesBtn,
  isPending,
}: GovtWizardTakeActionImplementationsProps) => {
  const accountUrlPrefix = useAccountUrlPrefix()
  const location = useLocation()
  const isMaxWidthLg = useMediaQuery("(max-width: 1024px)")

  const { management, bookConsult, setManagement, setBookConsult } =
    useGovernmentStore()

  return (
    <div className="pt-10 lg:pt-13 pb-[202px] sm:pb-31">
      <WizardHeader
        heading={
          account?.is_member
            ? "Would you like to chat with one of our NCX qualified Foresters to review?"
            : "How much help do you want along the process?"
        }
        subheading={
          account?.is_member
            ? "As a NCX Member, you will get an action plan in your Dashboard, but you can also take advantage of a consult for free."
            : undefined
        }
      />

      {!account?.is_member ? (
        <>
          <div className="container w-[996px] max-w-full grid grid-cols-1 md:grid-cols-2 gap-6 px-5 lg:px-10 mt-10 md:mt-16">
            <TakeActionCard
              selected={management === "self"}
              heading="Manage it myself"
              subheading="Continue with my free account"
              list={[
                "Use NCX to explore opportunities for your land’s potential.",
                "Review eligible programs and apply at your local USDA office.",
                "Implement your plan at your own pace. Locate your own contractors",
                "Keep track of land and status by myself",
              ]}
              listItemClass="!text-charcoal-500 !text-base !leading-[140%] tracking-[0.32px]"
              onClick={() => setManagement("self")}
              className="govt-app-manage-it-myself-action pl-[28px] pr-[30px] py-[18px]"
            />

            <TakeActionCard
              selected={management === "member"}
              badge={isMaxWidthLg ? "Most Popular" : undefined}
              darkBadge
              heading="Become NCX Member"
              subheading="$12/mo to access all our plans, programs and monitoring "
              list={[
                "Keep track of your selected stack of programs in our app for ease and organization.",
                "Save time with downloadable PDF to speed up NRCS application process.",
                "We give you the tools and step-by-step action plan to make your application easy.",
              ]}
              listItemClass="!text-charcoal-500 !text-base !leading-[140%] tracking-[0.32px]"
              onClick={() => setManagement("member")}
              className="govt-app-become-ncx-member-action pl-[28px] pr-[32px] py-[18px]"
            >
              <Link
                to={`${accountUrlPrefix}/become-a-member`}
                className="link mt-4 ml-[30px]"
                state={{ scrollToTop: true, prevPath: location.pathname }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                Learn More
              </Link>

              {!isMaxWidthLg ? <CornerRibbon text="Most Popular" dark /> : null}
            </TakeActionCard>
          </div>

          <div className="container w-[996px] max-w-full px-5 lg:px-10 my-[18px]">
            <div className="relative flex justify-center">
              <div className="absolute top-1/2 left-0 right-0 h-[1px] w-full bg-dusk-100" />

              <p className="relative bg-white inline-block text-base font-body leading-[140%] tracking-[0.32px] px-5">
                Add to either option
              </p>
            </div>
          </div>
        </>
      ) : null}

      <div
        className={cx("container w-[996px] max-w-full px-5 lg:px-10", {
          "mt-[46px]": account?.is_member,
        })}
      >
        <TakeActionCard
          selected={bookConsult}
          badge={isMaxWidthLg ? "Landowner Loved" : undefined}
          heading="Book Free NCX Consult"
          subheading="We love to help! Book a 30 min session with an NCX expert"
          list={[
            "We will help you understand your risks and the tradeoffs of different plan actions.",
            "We will assist you in pulling together all the paperwork and prepare you for the conversation with the officer. ",
          ]}
          listItemClass="!text-charcoal-500 !text-base !leading-[140%] tracking-[0.32px]"
          onClick={() => setBookConsult(!bookConsult)}
          className="govt-app-book-free-ncx-consult-action lg:pr-[70px] lg:pb-[54px]"
          columns
        >
          {!isMaxWidthLg ? (
            <CornerRibbon text="Landowner Loved" size="lg" />
          ) : null}
        </TakeActionCard>
      </div>

      <WizardFooter scrollToBottom={false}>
        <p className="hidden md:flex md:items-center text-overline">
          {account?.is_member
            ? "Free consult offer"
            : "Select an option to continue"}
        </p>

        <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-4 sm:ml-auto">
          <button
            type="button"
            className={cx(
              "govt-app-page-4-implementations-go-back-button font-bold tracking-[0.32px]",
              {
                "btn2 btn2-outline-primary": !account?.is_member,
                "link hover:cursor-pointer": account?.is_member,
              }
            )}
            onClick={handlePrevious}
          >
            Go Back
          </button>

          {account?.is_member ? (
            <>
              <SubmitButton
                className="govt-app-page-4-implementations-no-thanks-button btn2 btn2-outline-primary font-bold tracking-[0.32px]"
                onClick={() => handleSubmit()}
                isSubmitting={isPending && !isYesBtn}
                disabled={isPending && isYesBtn}
                typeButton
              >
                No Thanks
              </SubmitButton>

              <SubmitButton
                className="govt-app-page-4-implementations-yes-please-button btn2 btn2-primary font-bold tracking-[0.32px]"
                onClick={() => handleSubmit(true)}
                isSubmitting={isPending && isYesBtn}
                disabled={isPending && !isYesBtn}
                typeButton
              >
                Yes, Please!
              </SubmitButton>
            </>
          ) : (
            <SubmitButton
              className="govt-app-page-4-implementations-save-and-continue-button btn2 btn2-primary font-bold tracking-[0.32px]"
              onClick={() => handleSubmit()}
              isSubmitting={isPending}
              disabled={!management}
              typeButton
            >
              Save and Continue
            </SubmitButton>
          )}
        </div>
      </WizardFooter>
    </div>
  )
}

export default GovtWizardTakeActionImplementations
